import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { saveDesignAsset } from './designAssetService'
import { saveFormDefinition } from './formsService'
import type { TemplateRegistryEntry } from '../types/templates'
import type {
  DesignAssetDraft,
  DesignConnectorArrow,
  DesignShape,
} from '../types/design'
import { DEFAULT_DESIGN_CANVAS, DEFAULT_TEXT_STYLE } from '../types/design'
import type { FormDefinitionDraft, FormField } from '../types/forms'
import type { AutomationAction, AutomationGraph, NotifyUserActionConfig } from '../types/automation'

// ---------------------------------------------------------------------------
// Per-category "materializer" functions. Each takes a bundled explore template
// and writes a *live, editable* entity into the target surface's own
// collection so the imported item is fully usable there — not just a
// generic clone in `workspaces/*/templates`.
//
// Called from exploreRegistryService.importTemplate as a best-effort follow-up
// to the clone transaction: the clone tx still runs first (so the version /
// "saved" state stays consistent), then a materializer writes the surface-
// native draft. Materializer failures are logged but don't reverse the clone
// — the user can retry the import safely (both writes are id-idempotent).
// ---------------------------------------------------------------------------

export const AUTOMATION_GRAPHS_COLLECTION = 'automation_graphs'

export type MaterializeActor = { id: string; name?: string | null }

export const stableId = (workspaceId: string, sourceTemplateId: string, kind: string) =>
  `${kind}_${workspaceId}_${sourceTemplateId}`.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 128)

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])
const asString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback)

// --- Forms ------------------------------------------------------------------

export const FIELD_TYPE_MAP: Record<string, FormField['type']> = {
  text: 'TEXT',
  string: 'TEXT',
  textarea: 'TEXT',
  paragraph: 'TEXT',
  number: 'NUMBER',
  int: 'NUMBER',
  date: 'DATE',
  select: 'DROPDOWN',
  dropdown: 'DROPDOWN',
  radio: 'DROPDOWN',
  checkbox: 'CHECKBOX',
  boolean: 'CHECKBOX',
  file: 'FILE_UPLOAD',
  upload: 'FILE_UPLOAD',
  user: 'PERSON',
}

export const buildFormField = (raw: Record<string, unknown>, order: number): FormField => {
  const rawType = asString(raw.type, 'text').toLowerCase()
  const type = FIELD_TYPE_MAP[rawType] ?? 'TEXT'
  const id = asString(raw.id, `field-${order + 1}`)
  const label = asString(raw.label, `Field ${order + 1}`)
  const required = raw.required === true
  const base = { id, label, required, order, description: '' }
  if (type === 'PERSON') {
    return {
      ...base,
      type: 'PERSON',
      allowMultiple: raw.allowMultiple === true,
      validation: {},
    }
  }
  if (type === 'DROPDOWN') {
    const opts = asArray<unknown>(raw.options)
    return {
      ...base,
      type: 'DROPDOWN',
      placeholder: asString(raw.placeholder, 'Select an option'),
      presentation: 'SELECT',
      options: (opts.length > 0 ? opts : ['Option 1']).map((entry, idx) => {
        const label = typeof entry === 'string' ? entry : asString((entry as Record<string, unknown>)?.label, `Option ${idx + 1}`)
        return { id: `${id}-opt-${idx + 1}`, label, value: label.toLowerCase().replace(/\s+/g, '-') }
      }),
      validation: {},
    }
  }
  if (type === 'CHECKBOX') return { ...base, type: 'CHECKBOX', checkboxLabel: label, validation: {} }
  if (type === 'DATE') return { ...base, type: 'DATE', validation: {} }
  if (type === 'NUMBER') return { ...base, type: 'NUMBER', placeholder: '', validation: {} }
  if (type === 'FILE_UPLOAD') {
    return {
      ...base,
      type: 'FILE_UPLOAD',
      validation: {
        acceptedMimeTypes: [],
        maxFileSizeBytes: 10 * 1024 * 1024,
        maxFiles: 1,
      },
    }
  }
  return {
    ...base,
    type: 'TEXT',
    placeholder: asString(raw.placeholder, ''),
    multiline: rawType === 'textarea' || rawType === 'paragraph',
    validation: {},
  }
}

export const materializeForm = async (
  workspaceId: string,
  template: TemplateRegistryEntry,
  actor: MaterializeActor,
): Promise<string> => {
  const payload = (template.previewData.payload ?? {}) as Record<string, unknown>
  const rawFields = asArray<Record<string, unknown>>(payload.fields)
  const fields: FormField[] = rawFields.map((raw, idx) => buildFormField(raw, idx))
  const draft: FormDefinitionDraft = {
    id: stableId(workspaceId, template.id, 'form'),
    workspaceId,
    title: template.title,
    description: template.description || template.previewData.summary || '',
    fields,
    logic: { version: 1, rules: [] },
    theme: {},
    lifecycleStatus: 'DRAFT',
    deadline: null,
    isLocked: false,
    createdBy: actor.id,
    lastSubmissionResponseId: null,
  }
  return saveFormDefinition(draft, { id: actor.id, name: actor.name ?? 'Workspace member' })
}

// --- Designs ----------------------------------------------------------------

const SHAPE_DEFAULTS = {
  rotation: 0,
  opacity: 1,
  fill: { kind: 'SOLID', color: 'rgba(56,189,248,0.14)' } as const, // mosaic-keep: data (default shape fill color)
  stroke: { color: '#7dd3fc', width: 1.5, style: 'SOLID' as const }, // mosaic-keep: data (default shape stroke color)
  effects: [],
  blendMode: 'NORMAL' as const,
  textStyle: DEFAULT_TEXT_STYLE,
}

const buildDesignShapes = (payload: Record<string, unknown>): DesignShape[] => {
  const nodes = asArray<Record<string, unknown>>(payload.nodes)
  const connectors = asArray<Record<string, unknown>>(payload.connectors)
  const shapes: DesignShape[] = []
  for (const node of nodes) {
    const id = asString(node.id, `node-${shapes.length + 1}`)
    const kind = asString(node.type, 'stage').toLowerCase()
    const x = Number(node.x) || 0
    const y = Number(node.y) || 0
    const label = asString(node.label, '')
    if (kind === 'decision') {
      shapes.push({
        id,
        ...SHAPE_DEFAULTS,
        type: 'DIAMOND',
        position: { x, y },
        size: { width: 160, height: 96 },
        text: label,
      })
    } else {
      shapes.push({
        id,
        ...SHAPE_DEFAULTS,
        type: 'PROCESS',
        position: { x, y },
        size: { width: 180, height: 64 },
        text: label,
      })
    }
  }
  for (const [idx, conn] of connectors.entries()) {
    const fromId = asString(conn.from)
    const toId = asString(conn.to)
    if (!fromId || !toId) continue
    shapes.push({
      id: `connector-${idx + 1}`,
      ...SHAPE_DEFAULTS,
      fill: { kind: 'NONE' },
      type: 'CONNECTOR',
      position: { x: 0, y: 0 },
      size: { width: 1, height: 1 },
      from: { kind: 'SHAPE', shapeId: fromId, port: 'AUTO' },
      to: { kind: 'SHAPE', shapeId: toId, port: 'AUTO' },
      routing: 'ORTHOGONAL',
      arrowStart: 'NONE' as DesignConnectorArrow,
      arrowEnd: 'TRIANGLE' as DesignConnectorArrow,
    })
  }
  return shapes
}

export const materializeDesign = async (
  workspaceId: string,
  template: TemplateRegistryEntry,
  actor: MaterializeActor,
): Promise<string> => {
  const payload = (template.previewData.payload ?? {}) as Record<string, unknown>
  const draft: DesignAssetDraft = {
    id: stableId(workspaceId, template.id, 'design'),
    workspaceId,
    title: template.title,
    mode: asString(payload.designMode) === 'BOUND' ? 'BOUND' : 'FREE',
    canvas: (payload.canvas && typeof payload.canvas === 'object')
      ? { ...DEFAULT_DESIGN_CANVAS, ...(payload.canvas as Record<string, unknown>) } as DesignAssetDraft['canvas']
      : DEFAULT_DESIGN_CANVAS,
    shapes: buildDesignShapes(payload),
    templateOf: template.id,
  }
  await saveDesignAsset(draft, { id: actor.id, name: actor.name ?? 'Workspace member' })
  return draft.id
}

// --- Automations (v2 AutomationGraph) --------------------------------------

export const buildAutomationGraphDoc = (
  workspaceId: string,
  template: TemplateRegistryEntry,
  actor: MaterializeActor,
  timeZone?: string,
): Omit<AutomationGraph, 'id' | 'createdAt' | 'updatedAt' | 'lastRun'> => {
  const payload = (template.previewData.payload ?? {}) as Record<string, unknown>
  const graph = (payload.graph && typeof payload.graph === 'object' ? payload.graph : {}) as Record<string, unknown>
  const automationName = asString(graph.name, template.title)

  const normalizeNotifyActions = (actions: AutomationAction[]): AutomationAction[] => {
    return actions.map((act) => {
      if (act.type !== 'NOTIFY_USER') return act
      const cfg = (act.config ?? {}) as Record<string, unknown>
      const recipientUserId = typeof cfg.recipientUserId === 'string'
        ? cfg.recipientUserId
        : typeof cfg.userId === 'string'
          ? cfg.userId
          : undefined
      const body = typeof cfg.body === 'string'
        ? cfg.body
        : typeof cfg.message === 'string'
          ? cfg.message
          : undefined
      const title = typeof cfg.title === 'string' && cfg.title.trim()
        ? cfg.title
        : automationName
      const actionResult: AutomationAction = {
        id: act.id,
        type: 'NOTIFY_USER',
        config: {
          title,
          ...(recipientUserId ? { recipientUserId } : {}),
          ...(body ? { body } : {}),
          ...('recipientRole' in cfg ? { recipientRole: cfg.recipientRole as NotifyUserActionConfig['recipientRole'] } : {}),
        },
      }
      return actionResult
    })
  }

  const normalizeTriggers = (triggers: AutomationGraph['triggers']): AutomationGraph['triggers'] => {
    return triggers.map((trig) => {
      if (trig.type !== 'DATE_REACHED') return trig
      const cfg = (trig.config ?? {}) as Record<string, unknown>
      if (!cfg.cron || typeof cfg.cron !== 'string') return trig
      if (cfg.timeZone) return trig
      if (!timeZone) return trig
      return {
        ...trig,
        config: {
          ...cfg,
          timeZone,
        },
      }
    })
  }

  const thenActions = normalizeNotifyActions(asArray<AutomationAction>(graph.then))
  const elseActions = graph.else ? normalizeNotifyActions(asArray<AutomationAction>(graph.else)) : undefined

  return {
    workspaceId,
    name: automationName,
    description: asString(graph.description, template.description),
    version: 2,
    triggers: normalizeTriggers(asArray(graph.triggers)),
    ...(graph.constraint && typeof graph.constraint === 'object'
      ? { constraint: graph.constraint as AutomationGraph['constraint'] }
      : {}),
    ...(graph.if && typeof graph.if === 'object'
      ? { if: graph.if as AutomationGraph['if'] }
      : {}),
    then: thenActions,
    ...(elseActions ? { else: elseActions } : {}),
    // Templates always land PAUSED so users can review before firing.
    status: 'PAUSED',
    createdBy: actor.id,
  }
}

export const materializeAutomation = async (
  workspaceId: string,
  template: TemplateRegistryEntry,
  actor: MaterializeActor,
): Promise<string> => {
  const doc = buildAutomationGraphDoc(workspaceId, template, actor)
  const ref = await addDoc(collection(firestoreDb, AUTOMATION_GRAPHS_COLLECTION), {
    ...doc,
    sourceTemplateId: template.id,
    sourceRegistryVersion: template.registryVersion,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastRun: null,
  })
  return ref.id
}

// --- Dispatch ---------------------------------------------------------------

export const materializeTemplate = async (
  workspaceId: string,
  template: TemplateRegistryEntry,
  actor: MaterializeActor,
): Promise<{ collection: string; targetId: string } | null> => {
  switch (template.category) {
    case 'Forms':       return { collection: 'form_definitions', targetId: await materializeForm(workspaceId, template, actor) }
    case 'Designs':     return { collection: 'design_assets', targetId: await materializeDesign(workspaceId, template, actor) }
    case 'Automations': return { collection: AUTOMATION_GRAPHS_COLLECTION, targetId: await materializeAutomation(workspaceId, template, actor) }
    default:            return null
  }
}
